import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { buildEndpoint, buildQueryUrl, defaultDataTransform, useApiWorker, useDataFetcher, useDataPoster, useHasPermissionsV3 } from 'Utilities';
import { useToasts, useEnums } from 'Context';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { AsyncStatus } from 'Models';
import { ConfirmDialog } from 'v3/Components/Shared/ConfirmDialog';
import { ReviewerNote, ReviewerNoteDetail, ReviewerNoteThreadItem } from 'v3/Models/ReviewerNotes';
import { NoteDrawer } from './ReviewerProfile.NoteDrawer';
import { CommentDrawer } from './ReviewerProfile.CommentDrawer';
import { NoteList, NoteTypeFilterOption } from './ReviewerProfile.NoteList';
import { NoteDetailCard, NoteDetailSkeleton } from './ReviewerProfile.NoteDetail';
import './ReviewerProfile.Notes.scss';

const ReviewerProfileNotes: React.FC = () => {
  const { id: reviewerId } = useParams<{ id: string }>();
  const toast = useToasts();
  const API = useApiWorker('3.0');
  const hasPermission = useHasPermissionsV3();
  const canDeleteComment = hasPermission(PermissionCodesV3.Reviewer.Note.DeleteComment);
  const canDeleteNote = hasPermission(PermissionCodesV3.Reviewer.Note.Delete);
  const canAddNote = hasPermission(PermissionCodesV3.Reviewer.Note.Create);
  const canEditBody = hasPermission(PermissionCodesV3.Reviewer.Note.EditBody);
  const { reviewerNoteType } = useEnums();
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<ReviewerNote | null>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | undefined>();
  const [notes, setNotes] = useState<ReviewerNote[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [commentDrawerOpen, setCommentDrawerOpen] = useState(false);
  const [editingComment, setEditingComment] = useState<ReviewerNoteThreadItem | null>(null);
  const [savingComment, setSavingComment] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);
  const [commentDeleteModalOpen, setCommentDeleteModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const typeFilterOptions: NoteTypeFilterOption[] = useMemo(
    () =>
      Object.values(reviewerNoteType ?? {}).map((option) => ({
        value: Number(option.value),
        displayName: option.displayName ?? '',
      })),
    [reviewerNoteType]
  );

  // EnumerationProvider blocks rendering the app until enums are loaded, so reviewerNoteType
  // is already populated here — safe to seed the default (all active) selection synchronously.
  const [activeTypeValues, setActiveTypeValues] = useState<number[]>(() =>
    typeFilterOptions.map((option) => option.value)
  );
  const [systemActive, setSystemActive] = useState(true);

  const handleToggleType = (value: number) => {
    setActiveTypeValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleToggleSystem = () => setSystemActive((prev) => !prev);

  const handleApplyQuickFilters = () => {
    setActiveTypeValues(typeFilterOptions.map((option) => option.value));
    setSystemActive(true);
  };

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchInput), 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const notesUrl = useMemo(() => {
    const base = buildQueryUrl(buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'notes'), {
      search: debouncedSearch ? encodeURIComponent(debouncedSearch.trim()) : undefined,
    });
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}types=${encodeURIComponent(activeTypeValues.join(','))}&isSystem=${systemActive}`;
  }, [reviewerId, debouncedSearch, activeTypeValues, systemActive]);

  const [fetchedNotes, notesStatus] = useDataFetcher<ReviewerNote[]>(
    notesUrl,
    [],
    defaultDataTransform,
    undefined,
    !!reviewerId,
    '3.0'
  );

  // debouncedSearch !== searchInput means a keystroke hasn't been reflected in the query yet
  // (or the debounce timer hasn't fired) — treat that gap as loading too, otherwise `notes`
  // still reflects the *previous* search term while `searchInput` has already moved on,
  // which briefly shows stale/empty results under the new (mismatched) search term.
  const isNotesLoading =
    notesStatus === AsyncStatus.Idle ||
    notesStatus === AsyncStatus.Pending ||
    debouncedSearch !== searchInput;

  useEffect(() => {
    if (!isNotesLoading) setNotes(fetchedNotes);
  }, [isNotesLoading, fetchedNotes]);

  // Keeps the selection in sync with the (search/filter-narrowed) `notes` list: if the
  // selected note gets filtered out (or there was none), fall back to the first result;
  // if the list is now empty, clear the selection so the detail pane shows its empty state.
  useEffect(() => {
    if (isNotesLoading) return;
    if (notes.length === 0) {
      if (selectedNoteId) setSelectedNoteId(undefined);
      return;
    }
    if (!selectedNoteId || !notes.some((note) => note.id === selectedNoteId)) {
      setSelectedNoteId(notes[0].id);
    }
  }, [isNotesLoading, notes, selectedNoteId]);

  const [fetchedNoteDetail, noteDetailStatus, { getData: refetchNoteDetail }] = useDataFetcher<ReviewerNoteDetail | null>(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'notes', selectedNoteId),
    null,
    defaultDataTransform,
    undefined,
    !!reviewerId && !!selectedNoteId,
    '3.0'
  );

  const isNoteDetailLoading =
    !!selectedNoteId &&
    (noteDetailStatus === AsyncStatus.Idle || noteDetailStatus === AsyncStatus.Pending);

  // Mirrors the `notes` list pattern: local state seeded from the fetch lets comment
  // adds patch the thread in place instead of forcing a refetch/loading flicker.
  const [noteDetail, setNoteDetail] = useState<ReviewerNoteDetail | null>(null);
  useEffect(() => {
    if (!isNoteDetailLoading) setNoteDetail(fetchedNoteDetail);
  }, [isNoteDetailLoading, fetchedNoteDetail]);

  const selectedNote: ReviewerNoteDetail | null =
    !isNoteDetailLoading && selectedNoteId ? noteDetail : null;

  // The note body (first comment) can never be deleted on its own — only by deleting the note.
  const isEditingBodyEntry =
    !!selectedNote && !!editingComment && editingComment.id === selectedNote.commentId;

  const handleAddNote = () => {
    setEditingNote(null);
    setAddNoteOpen(true);
  };

  const handleEditNote = () => {
    if (!selectedNote) return;
    setEditingNote(selectedNote);
    setAddNoteOpen(true);
  };

  const handleDrawerClose = () => {
    setAddNoteOpen(false);
    setEditingNote(null);
  };

  const handleSaveSuccess = (note: ReviewerNote) => {
    const wasEditing = !!editingNote;
    setNotes((prev) => (wasEditing ? prev.map((n) => (n.id === note.id ? note : n)) : [note, ...prev]));
    setAddNoteOpen(false);
    setEditingNote(null);

    if (wasEditing && note.id === selectedNoteId) {
      refetchNoteDetail();
    } else {
      setSelectedNoteId(note.id);
    }
  };

  const handleConfirmDeleteNote = async () => {
    if (!selectedNoteId) return;
    setDeleting(true);
    try {
      const result: any = await API.delete(
        buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'notes', selectedNoteId)
      );
      if (result?.status >= 200 && result?.status < 300) {
        const deletedId = selectedNoteId;
        setNotes((prev) => prev.filter((n) => n.id !== deletedId));
        setSelectedNoteId((prev) =>
          prev === deletedId ? notes.find((n) => n.id !== deletedId)?.id : prev
        );
        toast.add('Note was deleted successfully', { type: 'success', position: 'bottom-left' });
        setDeleteModalOpen(false);
        handleDrawerClose();
      } else {
        toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
      }
    } catch {
      toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
    } finally {
      setDeleting(false);
    }
  };

  const [postComment] = useDataPoster(
    buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'notes', selectedNoteId, 'comments'),
    undefined,
    false,
    '3.0'
  );

  const handleOpenAddComment = () => {
    setEditingComment(null);
    setCommentDrawerOpen(true);
  };

  const findThreadItem = (commentId: string): ReviewerNoteThreadItem | undefined => {
    if (!selectedNote) return undefined;
    return commentId === selectedNote.commentId
      ? {
          id: selectedNote.commentId,
          comment: selectedNote.comment,
          dateCreated: selectedNote.dateCreated,
          createdBy: selectedNote.createdBy,
        }
      : selectedNote.comments.find((c) => c.id === commentId);
  };

  const handleOpenEditComment = (commentId: string) => {
    const item = findThreadItem(commentId);
    if (!item) return;
    setEditingComment(item);
    setCommentDrawerOpen(true);
  };

  const handleOpenDeleteComment = (commentId: string) => {
    const item = findThreadItem(commentId);
    if (!item) return;
    setEditingComment(item);
    setCommentDeleteModalOpen(true);
  };

  const handleCommentDrawerClose = () => {
    setCommentDrawerOpen(false);
    setEditingComment(null);
  };

  const handleEditCommentSubmit = async (comment: string) => {
    if (!editingComment || !selectedNoteId || !selectedNote) return;
    const noteId = selectedNoteId;
    const commentId = editingComment.id;
    const isBodyEntry = commentId === selectedNote.commentId;

    setSavingComment(true);
    try {
      const resp: any = await postComment({ id: commentId, comment });
      if (resp?.status === 200 || resp?.status === 201) {
        setNoteDetail((prev) => {
          if (!prev) return prev;
          return isBodyEntry
            ? { ...prev, comment }
            : { ...prev, comments: prev.comments.map((c) => (c.id === commentId ? { ...c, comment } : c)) };
        });
        if (isBodyEntry) {
          setNotes((prev) => prev.map((n) => (n.id === noteId ? { ...n, comment } : n)));
        }
        toast.add('Changes were saved successfully.', { type: 'success', position: 'bottom-left' });
        handleCommentDrawerClose();
      } else {
        toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
      }
    } catch {
      toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
    } finally {
      setSavingComment(false);
    }
  };

  const handleAddCommentSubmit = async (comment: string) => {
    if (!selectedNoteId || !selectedNote) return;
    const noteId = selectedNoteId;
    // When the body was previously cleared out (all comments deleted), the backend promotes
    // the new comment into the body slot rather than appending it to the thread — mirror that
    // here or the stale empty body entry keeps rendering alongside the newly added comment.
    const wasEmpty = selectedNote.commentCount === 0;

    setSavingComment(true);
    try {
      const resp: any = await postComment({ id: null, comment });
      if (resp?.status === 200 || resp?.status === 201) {
        const saved = resp.data as ReviewerNoteThreadItem;
        setNoteDetail((prev) => {
          if (!prev) return prev;
          const commentCount = prev.commentCount + 1;
          return wasEmpty
            ? { ...prev, commentCount, comment: saved.comment, commentId: saved.id }
            : { ...prev, commentCount, comments: [...prev.comments, saved] };
        });
        setNotes((prev) =>
          prev.map((n) =>
            n.id === noteId
              ? { ...n, commentCount: n.commentCount + 1, ...(wasEmpty ? { comment: saved.comment } : {}) }
              : n
          )
        );
        toast.add('Comment was added successfuly', { type: 'success', position: 'bottom-left' });
        handleCommentDrawerClose();
      } else {
        toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
      }
    } catch {
      toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
    } finally {
      setSavingComment(false);
    }
  };

  const handleCommentSubmit = (comment: string) =>
    editingComment ? handleEditCommentSubmit(comment) : handleAddCommentSubmit(comment);

  const handleDeleteComment = async () => {
    if (!selectedNoteId || !selectedNote || !editingComment) return;
    const noteId = selectedNoteId;
    const commentId = editingComment.id;
    const isBodyEntry = commentId === selectedNote.commentId;
    // Deleting the body promotes the next-oldest comment (comments is sorted oldest-first)
    // to take its place, mirroring the backend's Comment/CommentId/Comments mapping.
    const promoted = isBodyEntry ? selectedNote.comments[0] : undefined;

    setDeletingComment(true);
    try {
      const result: any = await API.delete(
        buildEndpoint(ENDPOINTS.reviewers.base, reviewerId, 'notes', noteId, 'comments', commentId)
      );
      if (result?.status >= 200 && result?.status < 300) {
        setNoteDetail((prev) => {
          if (!prev) return prev;
          const commentCount = Math.max(0, prev.commentCount - 1);
          return isBodyEntry
            ? {
                ...prev,
                commentCount,
                comment: promoted?.comment ?? '',
                commentId: promoted?.id ?? '',
                comments: prev.comments.slice(1),
              }
            : { ...prev, commentCount, comments: prev.comments.filter((c) => c.id !== commentId) };
        });
        setNotes((prev) =>
          prev.map((n) =>
            n.id === noteId
              ? {
                  ...n,
                  commentCount: Math.max(0, n.commentCount - 1),
                  ...(isBodyEntry ? { comment: promoted?.comment ?? '' } : {}),
                }
              : n
          )
        );
        toast.add('Comment was deleted successfully', { type: 'success', position: 'bottom-left' });
        setCommentDeleteModalOpen(false);
        handleCommentDrawerClose();
      } else {
        toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
      }
    } catch {
      toast.add('An error occurred. Please try again.', { type: 'error', position: 'bottom-left' });
    } finally {
      setDeletingComment(false);
    }
  };

  return (
    <div className="rp-notes">
      <NoteList
        notes={notes}
        isLoading={isNotesLoading}
        selectedNoteId={selectedNoteId}
        onSelectNote={setSelectedNoteId}
        onAddNote={handleAddNote}
        canAddNote={canAddNote}
        searchTerm={searchInput}
        onSearchChange={setSearchInput}
        typeFilterOptions={typeFilterOptions}
        activeTypeValues={activeTypeValues}
        systemActive={systemActive}
        onToggleType={handleToggleType}
        onToggleSystem={handleToggleSystem}
        onApplyQuickFilters={handleApplyQuickFilters}
      />
      <div className="rp-notes__detail-pane">
        {isNoteDetailLoading && <NoteDetailSkeleton />}
        {!isNoteDetailLoading && selectedNote && (
          <NoteDetailCard
            note={selectedNote}
            onAddComment={handleOpenAddComment}
            onEditNote={handleEditNote}
            onDeleteNote={() => setDeleteModalOpen(true)}
            onCommentMenuClick={handleOpenEditComment}
            onCommentDeleteClick={handleOpenDeleteComment}
          />
        )}
      </div>

      <NoteDrawer
        reviewerId={reviewerId}
        open={addNoteOpen}
        note={editingNote}
        deleting={deleting}
        canDelete={canDeleteNote && !editingNote?.isSystem}
        canEditBody={canEditBody}
        onClose={handleDrawerClose}
        onSuccess={handleSaveSuccess}
        onDelete={() => setDeleteModalOpen(true)}
      />

      <CommentDrawer
        open={commentDrawerOpen}
        initialComment={editingComment?.comment}
        saving={savingComment}
        deleting={deletingComment}
        canDelete={canDeleteComment && !isEditingBodyEntry}
        onClose={handleCommentDrawerClose}
        onSubmit={handleCommentSubmit}
        onDelete={() => setCommentDeleteModalOpen(true)}
      />

      <ConfirmDialog
        dialogVisible={deleteModalOpen}
        toggleDialog={() => setDeleteModalOpen(false)}
        title="Delete Note"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        isPrimaryDanger
        primaryButtonEnabled={!deleting}
        OnPrimaryButtonClick={handleConfirmDeleteNote}
        OnSecondaryButtonClick={() => setDeleteModalOpen(false)}
      >
        <div className="rp-notes__delete-modal-body">
          Are you sure you want to delete this note about the reviewer? This will remove the
          entire note (including all related comments) permanently from the system.
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        dialogVisible={commentDeleteModalOpen}
        toggleDialog={() => setCommentDeleteModalOpen(false)}
        title="Delete Comment"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        isPrimaryDanger
        primaryButtonEnabled={!deletingComment}
        OnPrimaryButtonClick={handleDeleteComment}
        OnSecondaryButtonClick={() => setCommentDeleteModalOpen(false)}
      >
        <div className="rp-notes__delete-modal-body">
          Are you sure you want to delete this comment about the reviewer? This will only remove
          the comment from the note (not the entire note thread) permanently from the system.
        </div>
      </ConfirmDialog>
    </div>
  );
};

export default ReviewerProfileNotes;
